// ── 상황판 관리자 위젯 데이터 훅 (2026-06-06) ───────────────────────────────
//
// visibleCodes (admin_ 접두사) 의 데이터를 한 번에 batch 로 fetch.
// 위젯별 month state (monthMap) 지원 — chevron 클릭 시 변경.
// API: GET /api/admin/widgets/snapshot?codes=...&month_<code>=YYYY-MM&...
// 응답: { success, allowed: [...], data: { code: {...} } }

import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { ADMIN_WIDGET_SPECS } from './AdminWidgetsSpec';

// 어떤 위젯이 월 navigate 지원? spec.monthNavigate 기준
const MONTH_AWARE_CODES = new Set(
  Object.entries(ADMIN_WIDGET_SPECS)
    .filter(([, spec]) => spec && spec.monthNavigate === true)
    .map(([code]) => code),
);

export function useAdminWidgets({ visibleCodes = [], enabled = true }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [allowed, setAllowed] = useState([]);
  // 위젯별 월 state — 기본 undefined → 서버 기본(오늘 KST) 사용
  const [monthMap, setMonthMap] = useState({});

  const adminCodes = useMemo(
    () => (visibleCodes || []).filter((c) => typeof c === 'string' && c.startsWith('admin_')),
    [visibleCodes],
  );
  const adminCodesKey = adminCodes.join('|');

  // monthMap 안정 키 — 위젯 ↔ 월 변경 시 refetch
  const monthMapKey = useMemo(() => {
    const parts = [];
    for (const code of adminCodes) {
      if (MONTH_AWARE_CODES.has(code) && monthMap[code]) {
        parts.push(`${code}=${monthMap[code]}`);
      }
    }
    return parts.join('|');
  }, [adminCodes, monthMap]);

  const fetchAll = useCallback(async () => {
    if (!enabled) return;
    if (!adminCodesKey) {
      setData({});
      setAllowed([]);
      return;
    }
    setLoading(true);
    try {
      const params = [`codes=${encodeURIComponent(adminCodesKey.replace(/\|/g, ','))}`];
      for (const code of adminCodesKey.split('|')) {
        if (MONTH_AWARE_CODES.has(code) && monthMap[code]) {
          params.push(`month_${encodeURIComponent(code)}=${encodeURIComponent(monthMap[code])}`);
        }
      }
      const url = `/api/admin/widgets/snapshot?${params.join('&')}`;
      const res = await apiClient.get(url);
      if (res.ok) {
        setData(res.data?.data || {});
        setAllowed(Array.isArray(res.data?.allowed) ? res.data.allowed : []);
      } else {
        setData({});
        setAllowed([]);
      }
    } catch (err) {
      console.error('[useAdminWidgets] fetch 실패:', err);
      setData({});
      setAllowed([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, adminCodesKey, monthMapKey]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const setMonthForCode = useCallback((code, ym) => {
    if (!MONTH_AWARE_CODES.has(code)) return;
    if (!/^\d{4}-\d{2}$/.test(String(ym || ''))) return;
    setMonthMap((prev) => ({ ...prev, [code]: ym }));
  }, []);

  return {
    loading,
    data,
    allowed,
    monthMap,
    setMonthForCode,
    refresh: fetchAll,
  };
}
